import { create } from "zustand";
import { WorkCalendar, type CalendarDefinition } from "@/engine/calendar";
import { computeSchedule, wouldCreateCycle } from "@/engine/cpm";
import { addDays, todayISO } from "@/engine/date";
import { diffSchedules, type ScheduleImpact } from "@/engine/diff";
import { analyzeHealth, computeEvm, createBaseline, topDelays, type DelayItem, type EvmSummary, type HealthReport } from "@/engine/health";
import { createEmptyProject } from "@/engine/sample";
import { sampleErpRecords } from "@/engine/sample-records";
import type { AttachmentLink, Baseline, ChecklistItem, CostOptions, CustomFieldDef, Dependency, DocumentBlock, DocumentKind, DocumentLinkTargetKind, DocumentVisibility, ExecutionStatus, ExecutionTask, GateChecklistItem, GateTagType, GateTag, IntakeItem, ISODate, KptCard, LabelColor, Project, ProjectBudget, ProjectRecord, ProjectResourceAssignment, RecordKind, Schedule, SolutionItem, Task, TaskLabel, WeeklyReportDraft, WeeklyReportSnapshot, WeeklySectionVisibility } from "@/engine/types";
import { addKptCard, normalizeRetrospective, upvoteKpt } from "@/features/retrospective/retrospective-model";
import { can, planActor, type MemberPlanRole } from "@/features/access/can";
import * as dops from "@/features/docs/document-ops";
import { documentWriteBlocked } from "@/features/docs/doc-model";
import { clampPxPerDay, nudgePxPerDay, pxPerDayFromPercent, resolvePxPerDay, resolveZoomPreset, ZOOM_PRESET_PX, type ZoomPreset } from "@/features/gantt/zoom";
import {
  buildReportFromDraft,
  createRevisionDraftFromSnapshot,
  DEFAULT_WEEKLY_SECTIONS,
  seedWeeklyDraft,
} from "@/features/weekly-report/weekly-draft";
import { findWeeklySnapshot, freezeWeeklyReport } from "@/features/weekly-report/weekly-snapshot";
import { GRID_DEFAULT_WIDTH } from "@/features/workspace/grid-splitter";
import { saveCatalogImmediately, useCatalogStore } from "./catalog-store";
import * as xops from "./execution-task-ops";
import * as iops from "./intake-ops";
import * as rops from "./record-ops";
import { formatDate } from "@/lib/date-format";
import { BASELINE_LIMIT_ERROR, prepareBaselineRename, prepareBaselineSave } from "@/lib/format";
import { normalizeProjectResources, type ProjectTab, type ThemePref } from "./persist";
import { buildDemoHistory, makeReportPeriod, REPORT_PERIOD_LIMIT, suggestPeriodName, type ReportPeriod } from "@/features/reports/report-model";
import * as ops from "./tree-ops";

/** @deprecated Prefer ZoomPreset from gantt/zoom — kept for call-site imports. */
export type Zoom = ZoomPreset;
export type Density = "compact" | "default" | "comfortable";
export type SidePanel = "inspector" | "insights";
export type ScheduleView = "gantt" | "sheet";

export interface Checkpoint {
  id: string;
  name: string;
  createdAt: string;
  project: Project;
}

export interface ChangeLogEntry {
  id: string;
  at: string;
  label: string;
  taskIds: string[];
  finishDeltaDays: number;
}

export interface Derived {
  calendar: WorkCalendar;
  schedule: Schedule;
  health: HealthReport;
  evm: EvmSummary;
  delays: DelayItem[];
  baseline: Baseline | null;
}

export type BarLabel = "name" | "dates" | "assignee" | "none";
export type PinBoundary = "name" | "wbs" | "none";

/** 기본 숨김: 담당·선행·태스크·남은 기간·메모. 기본 노출은 M/M, 기간, 시작, 종료, 여유, 진척. */
export const DEFAULT_HIDDEN_COLUMNS = ["assignee", "preds", "tasks", "remaining", "note"] as const;

const PIN_BOUNDARIES: PinBoundary[] = ["name", "wbs", "none"];

function isPinBoundary(v: unknown): v is PinBoundary {
  return typeof v === "string" && PIN_BOUNDARIES.includes(v as PinBoundary);
}

export interface UIState {
  /** 일정 화면 표시 방식: 표+간트 또는 전폭 시트 */
  scheduleView: ScheduleView;
  /** Active day/week/month mode — does not change when zooming ±%. */
  zoomPreset: ZoomPreset;
  /** Continuous horizontal scale (px per calendar day). 100% = ZOOM_PRESET_PX[zoomPreset]. */
  pxPerDay: number;
  density: Density;
  sidePanel: SidePanel | null;
  showBaseline: boolean;
  showCritical: boolean;
  showDependencies: boolean;
  /** status-date progress line (MS Project style) */
  showProgressLine: boolean;
  /** total-float extension after non-critical bars */
  showFloat: boolean;
  /** percent-complete text inside task bars */
  showProgressLabel: boolean;
  /** remaining working days to the left of summary brackets */
  showRemainingLabel: boolean;
  /** emphasize the driving path of the focused task, dim other links */
  highlightDrivingPath: boolean;
  barLabel: BarLabel;
  /** grid column keys hidden by the user */
  hiddenColumns: string[];
  /** left-freeze through this identity column */
  pinnedColumn: PinBoundary;
  theme: "system" | "light" | "dark";
  gridWidth: number;
  /** Simulated RBAC view role: PM sees rates/budgets, member has them masked */
  viewRole: "pm" | "member";
  /** Force masking of monetary rates and costs */
  maskRates: boolean;
}

interface State {
  /** catalog id currently loaded in the editor; null when nothing is open */
  activeId: string | null;
  project: Project;
  derived: Derived;
  past: Project[];
  /** 같은 인덱스의 스냅샷을 되돌릴 때 그 사이 저장된 본문을 남길지. */
  pastKeepBody: boolean[];
  future: Project[];
  futureKeepBody: boolean[];
  lastImpact: ScheduleImpact | null;
  changeLog: ChangeLogEntry[];
  checkpoints: Checkpoint[];
  /** closed report periods, newest first */
  reports: ReportPeriod[];
  selection: string[];
  focusId: string | null;
  collapsed: Set<string>;
  ui: UIState;
  /** 이 역할로 보기. null이면 로컬 소유자. 플랜 파일에는 저장하지 않는다. */
  previewPlanRole: MemberPlanRole | null;
  /** 워크스페이스 관리자(비멤버)로 보기. 플랜 파일에는 저장하지 않는다. */
  previewWorkspaceAdmin: boolean;
  /** 기여자 미리보기의 본인 배정. 플랜 파일에는 저장하지 않는다. */
  previewSelfResourceId: string | null;
  /** 게스트로 보기. 이 플랜만 보이고, 플랜 파일에는 저장하지 않는다. */
  previewGuest: boolean;
  /** 게스트로 보는 동안 열린 플랜. */
  previewGuestPlanId: string | null;
  /** 워크스페이스 참여자(뷰어)로 보기. 플랜 파일에는 저장하지 않는다. */
  previewWorkspaceMember: boolean;
  saveState: "saved" | "dirty" | "saving";
  lastError: string | null;
  /** one-shot request for the Gantt to scroll a date into view */
  scrollTo: { date: string; seq: number; align?: "center" | "third" } | null;
  /** one-shot: fit timeline into the gantt viewport */
  fitRequest: { mode: "project" | "selection"; seq: number } | null;
  /** app-level modal currently open */
  dialog: AppDialog | null;
  /** WBS 작업 삭제 확인 대기 중인 id 목록 */
  pendingDeleteTaskIds: string[] | null;
  /** execution task opened in the detail drawer */
  executionFocusId: string | null;
  /** whether the currently opened task was just created (in creation mode) */
  executionFocusIsNew: boolean;
  /** which drawer tab to show when the Task sheet opens */
  executionFocusTab: SheetTab;
  /** comment to scroll into view when the Task sheet opens on 댓글 */
  executionFocusCommentId: string | null;
  /** record the Task sheet was opened from; closing the Task sheet reopens it */
  executionReturnRecordId: string | null;
  /** execution tasks selected for bulk edit (tasks page) */
  executionSelection: string[];
  /** record (issue/decision/…) opened in the record drawer */
  recordFocusId: string | null;
  /** whether the open record was just created and is dropped on cancel */
  recordFocusIsNew: boolean;
  /** which drawer tab to show when the record sheet opens */
  recordFocusTab: SheetTab;
  /** comment to scroll into view when the record sheet opens on 댓글 */
  recordFocusCommentId: string | null;
  /** project tab last shown; persisted with the project chrome */
  lastTab: ProjectTab;
  /** schedule task opened over the current page. Not written to the plan file. */
  schedulePeekId: string | null;
}

export type AppDialog = "settings" | "status-update" | "palette" | "shortcuts" | "planner-settings" | "plan-settings" | "report-close" | "create-plan";

/** 작업·기록 드로어의 탭. 파일 탐색에서 첨부·댓글을 바로 열 때 쓴다. */
export type SheetTab = "details" | "comments" | "attachments" | "activity";

interface Actions {
  commit: (label: string, fn: (p: Project) => Project | null, opts?: { taskIds?: string[]; keepBody?: boolean }) => boolean;
  /** Undoable edit that only touches Planner data: no CPM recompute, no schedule impact card. */
  commitPlanner: (label: string, fn: (p: Project) => Project | null, opts?: { keepBody?: boolean }) => boolean;
  undo: () => void;
  redo: () => void;
  select: (ids: string[], focus?: string | null) => void;
  toggleSelect: (id: string) => void;
  toggleCollapse: (id: string) => void;
  setCollapsedAll: (collapsed: boolean) => void;
  setUI: (patch: Partial<UIState>) => void;
  clearError: () => void;
  scrollToDate: (date: string, align?: "center" | "third") => void;
  /** Snap to day/week/month at 100%. */
  setZoomPreset: (preset: ZoomPreset) => void;
  /** Multiply/divide scale by ZOOM_STEP (toolbar −/+). */
  nudgeZoom: (direction: 1 | -1) => void;
  /** Set scale as percent of the active preset (100 = preset px). */
  setZoomPercent: (percent: number) => void;
  /** Ask Gantt to fit project or selection into the viewport. */
  requestFit: (mode?: "project" | "selection") => void;
  openDialog: (dialog: AppDialog | null) => void;

  updateTask: (id: string, patch: Partial<Task>, label?: string) => void;
  /** apply several task patches as one undoable step */
  updateTasks: (patches: Array<{ id: string; patch: Partial<Task> }>, label: string) => void;
  addSibling: (afterId: string | null, partial?: Partial<Task>) => string;
  addChild: (parentId: string, partial?: Partial<Task>) => string;
  /** wrap the current selection in a new summary task */
  groupSelection: (name?: string) => string | null;
  /** 삭제 전 확인 다이얼로그를 연다. */
  requestDeleteTasks: (ids: string[]) => void;
  cancelDeleteTasks: () => void;
  deleteTasks: (ids: string[]) => void;
  indent: (ids: string[]) => void;
  outdent: (ids: string[]) => void;
  moveUp: (id: string) => void;
  moveDown: (id: string) => void;

  addDependency: (dep: Omit<Dependency, "id">) => boolean;
  updateDependency: (id: string, patch: Partial<Dependency>) => void;
  removeDependency: (id: string) => void;
  setPredecessorsFromText: (taskId: string, text: string) => boolean;
  linkSelection: () => void;

  setProject: (
    patch: Partial<
      Pick<Project, "name" | "start" | "statusDate" | "criticalThreshold" | "defaultProgressMethod" | "defaultDuration" | "insertStart" | "childInheritsParent" | "statusOverride">
    >,
  ) => void;
  setLifecycle: (lifecycle: "active" | "done" | "archived") => void;
  setRetrospectiveSummary: (summary: string) => void;
  addRetrospectiveCard: (draft: { column: KptCard["column"]; body: string; anonymous: boolean }) => void;
  upvoteRetrospectiveCard: (cardId: string) => void;
  setCalendar: (def: CalendarDefinition) => void;
  saveBaseline: (name?: string, note?: string) => void;
  setActiveBaseline: (id: string | null) => void;
  renameBaseline: (id: string, name: string, note?: string) => void;
  deleteBaseline: (id: string) => void;

  saveCheckpoint: (name?: string) => void;
  restoreCheckpoint: (id: string, keepProgress: boolean) => void;
  deleteCheckpoint: (id: string) => void;

  /** close the current plan as a report period; returns its id */
  closeReportPeriod: (name?: string, note?: string) => string;
  /** turn a checkpoint snapshot into a report period (checkpoints themselves stay undo-only) */
  promoteCheckpointToReport: (checkpointId: string, name?: string) => string | null;
  deleteReportPeriod: (id: string) => void;
  /** roll the plan back into three example weekly periods (adds a kickoff baseline when missing) */
  createDemoReports: () => void;
  clearDemoReports: () => void;

  /* ---- Weekly report draft & snapshot lifecycle ---- */
  ensureWeeklyDraft: (statusDate?: ISODate) => WeeklyReportDraft | null;
  updateWeeklyDraft: (statusDate: ISODate, updater: (draft: WeeklyReportDraft) => WeeklyReportDraft) => void;
  setWeeklySectionDefaults: (patch: Partial<WeeklySectionVisibility>) => void;
  closeWeeklyReport: (statusDate?: ISODate) => WeeklyReportSnapshot | null;
  openWeeklyRevision: (statusDate: ISODate) => WeeklyReportDraft | null;
  /** 그 기준일의 초안만 지운다. 마감본은 건드리지 않는다. */
  discardWeeklyDraft: (statusDate: ISODate) => void;

  loadSample: () => string;
  loadDemoCatalog: () => string;
  newProject: () => string;
  importProject: (json: string) => boolean;
  exportProject: () => string;
  openProject: (id: string) => boolean;
  closeProject: () => void;
  setLastTab: (tab: ProjectTab) => void;
  /** 일정 탭으로 들어갈 때 선택·사이드 패널·드로어·다이얼로그를 비운다. */
  resetEditorSession: () => void;

  /* ---- Planner execution tasks ---- */
  addExecutionTask: (partial?: Partial<ExecutionTask>, opts?: { open?: boolean }) => string;
  updateExecutionTask: (id: string, patch: Partial<ExecutionTask>, label?: string) => void;
  updateExecutionTasks: (ids: string[], patch: Partial<ExecutionTask>, label: string) => void;
  setExecutionStatus: (id: string, status: ExecutionStatus | undefined) => void;
  deleteExecutionTasks: (ids: string[]) => void;
  duplicateExecutionTask: (id: string) => string | null;
  /** move to a bucket (or none) and/or reorder within the column ids given in display order */
  moveExecutionTask: (id: string, patch: Partial<ExecutionTask>, columnIds: string[], beforeId: string | null) => void;
  addChecklistItem: (id: string, text: string, extra?: { assignee?: string; due?: ISODate }) => void;
  updateChecklistItem: (id: string, itemId: string, patch: Partial<ChecklistItem>) => void;
  removeChecklistItem: (id: string, itemId: string) => void;
  convertChecklistToTask: (id: string, itemId: string) => string | null;
  addTaskComment: (id: string, body: string, options?: { parentId?: string; attachments?: AttachmentLink[] }) => void;
  updateTaskComment: (id: string, commentId: string, body: string, options?: { attachments?: AttachmentLink[] }) => void;
  deleteTaskComment: (id: string, commentId: string) => void;
  addTaskAttachment: (id: string, title: string, url: string, extra?: { kind?: "link" | "file" | "image"; size?: number; mimeType?: string; addedBy?: string }) => void;
  removeTaskAttachment: (id: string, attachmentId: string) => void;
  /**
   * Open (id) or close (null) the Task sheet. `returnToRecordId` remembers the record sheet it was opened from;
   * closing then reopens that record instead of leaving the user with no sheet.
   */
  setExecutionFocus: (id: string | null, opts?: { isNew?: boolean; returnToRecordId?: string; tab?: SheetTab; commentId?: string }) => void;
  setExecutionSelection: (ids: string[]) => void;
  toggleExecutionSelection: (id: string) => void;

  /* ---- QA gate tags (ExecutionTask) ---- */
  addGateTag: (taskId: string, type: GateTagType, opts?: { name?: string; criteria?: string[] }) => void;
  removeGateTag: (taskId: string, gateId: string) => void;
  addGateChecklistItem: (taskId: string, gateId: string, text: string) => void;
  removeGateChecklistItem: (taskId: string, gateId: string, itemId: string) => void;
  toggleGateChecklistDev: (taskId: string, gateId: string, itemId: string) => void;
  requestGateReview: (taskId: string, gateId: string) => void;
  setGateChecklistQaStatus: (taskId: string, gateId: string, itemId: string, status: GateChecklistItem["qaStatus"], reason?: string) => void;
  setGateVerdict: (taskId: string, gateId: string, verdict: "pass" | "fail", reason?: string) => void;

  /* ---- Intake (triage airlock) ---- */
  addIntake: (partial: Partial<IntakeItem>) => string;
  updateIntake: (id: string, patch: Partial<IntakeItem>) => void;
  deleteIntakes: (ids: string[]) => void;
  startIntakeReview: (id: string) => void;
  closeIntake: (id: string, status: "rejected" | "deferred", note: string) => void;
  reopenIntake: (id: string) => void;
  /** 승인 → 신규 ExecutionTask. 만든 task id 를 돌려준다. */
  acceptIntakeAsTask: (id: string, opts?: Partial<Omit<iops.AcceptOptions, "by">>) => string | null;
  /** 승인 → 기존 ExecutionTask 에 게이트로 병합. */
  mergeIntakeIntoTask: (id: string, targetTaskId: string, opts?: Partial<Omit<iops.AcceptOptions, "by">>) => boolean;

  /* ---- Records (issues, risks, decisions, policies, change requests) ---- */
  addRecord: (kind: RecordKind, partial?: Partial<ProjectRecord>, opts?: { open?: boolean }) => string;
  updateRecord: (id: string, patch: Partial<ProjectRecord>) => void;
  deleteRecords: (ids: string[]) => void;
  createDocument: (kind?: DocumentKind) => string;
  saveDocumentBody: (id: string, blocks: DocumentBlock[]) => boolean;
  patchDocument: (id: string, patch: Partial<Pick<import("@/engine/types").PlanDocument, "title" | "kind" | "heldAt">>) => void;
  setDocumentVisibility: (id: string, visibility: DocumentVisibility) => void;
  trashDocument: (id: string) => void;
  restoreDocument: (id: string) => void;
  purgeDocument: (id: string) => void;
  linkDocument: (id: string, targetKind: DocumentLinkTargetKind, targetId: string) => void;
  unlinkDocument: (id: string, linkId: string) => void;
  setDocumentAttachments: (id: string, attachments: AttachmentLink[]) => void;
  addDocumentComment: (id: string, body: string, options?: { parentId?: string; attachments?: AttachmentLink[] }) => void;
  updateDocumentComment: (id: string, commentId: string, body: string, options?: { attachments?: AttachmentLink[] }) => void;
  deleteDocumentComment: (id: string, commentId: string) => void;
  createWorkFromDocument: (input: { documentId: string; kind: "task" | "issue"; title: string; blockId?: string }) => { taskId?: string; recordId?: string } | null;
  abandonBlankDocument: (id: string) => void;
  addRecordComment: (id: string, body: string, options?: { parentId?: string; attachments?: AttachmentLink[] }) => void;
  updateRecordComment: (id: string, commentId: string, body: string, options?: { attachments?: AttachmentLink[] }) => void;
  deleteRecordComment: (id: string, commentId: string) => void;
  /** new decision/policy that supersedes `id`; opens it */
  reviseRecord: (id: string) => string | null;
  /** register an issue for a risk that happened; opens it */
  raiseIssueFromRisk: (id: string) => string | null;
  /** create an execution task that answers the record, linked both ways */
  createTaskForRecord: (id: string, opts?: { open?: boolean; title?: string }) => string | null;
  /** Dev helper: add the ERP sample RAID log to the open plan (undoable, keeps existing records). */
  appendSampleRecords: () => number;
  setRecordFocus: (id: string | null, opts?: { isNew?: boolean; tab?: SheetTab; commentId?: string }) => void;
  /** Open the schedule inspector over the current page. Null closes it. */
  setSchedulePeek: (id: string | null) => void;

  addBucket: (name: string) => string;
  renameBucket: (id: string, name: string) => void;
  moveBucket: (id: string, direction: -1 | 1) => void;
  reorderBucket: (id: string, toIndex: number) => void;
  removeBucket: (id: string) => void;
  addLabel: (name: string, color: LabelColor) => string;
  updateLabel: (id: string, patch: Partial<TaskLabel>) => void;
  reorderLabel: (id: string, toIndex: number) => void;
  removeLabel: (id: string) => void;
  addCustomField: (def: Omit<CustomFieldDef, "id">) => string;
  updateCustomField: (id: string, patch: Partial<CustomFieldDef>) => void;
  removeCustomField: (id: string) => void;

  // Resource assignments & M/M tracking
  assignResource: (assignment: Omit<ProjectResourceAssignment, "id">) => string;
  updateAssignment: (id: string, patch: Partial<ProjectResourceAssignment>) => void;
  removeAssignment: (id: string) => void;

  // Solutions & licenses
  addSolution: (solution: Omit<SolutionItem, "id">) => string;
  updateSolution: (id: string, patch: Partial<SolutionItem>) => void;
  removeSolution: (id: string) => void;

  // Budget & cost options
  updateBudget: (patch: Partial<ProjectBudget>) => void;
  updateCostOptions: (patch: Partial<CostOptions>) => void;
  setViewRole: (role: "pm" | "member") => void;
  setMaskRates: (mask: boolean) => void;
  setPreviewPlanRole: (role: MemberPlanRole | null) => void;
  setPreviewWorkspaceAdmin: (on: boolean) => void;
  setPreviewSelfResourceId: (id: string | null) => void;
  setPreviewGuest: (on: boolean) => void;
  setPreviewWorkspaceMember: (on: boolean) => void;
}

const HISTORY_LIMIT = 200;

function wbsOf(schedule: Schedule): Map<string, string> {
  const map = new Map<string, string>();
  for (const [id, task] of schedule.tasks) map.set(id, task.wbs);
  return map;
}

function historyPush(s: Pick<State, "past" | "pastKeepBody" | "project">, keepBody: boolean) {
  const past = [...s.past.slice(-HISTORY_LIMIT + 1), s.project];
  const aligned = s.pastKeepBody.length === s.past.length ? s.pastKeepBody : s.past.map(() => true);
  return {
    past,
    pastKeepBody: [...aligned.slice(-HISTORY_LIMIT + 1), keepBody],
    future: [] as Project[],
    futureKeepBody: [] as boolean[],
  };
}

function derive(project: Project): Derived {
  const calendar = new WorkCalendar(project.calendar);
  const schedule = computeSchedule(project, calendar);
  const baseline = project.baselines.find((b) => b.id === project.activeBaselineId) ?? project.baselines.at(-1) ?? null;
  return {
    calendar,
    schedule,
    health: analyzeHealth(project, schedule),
    evm: computeEvm(project, schedule, baseline, calendar),
    delays: topDelays(project, schedule, baseline, 5, calendar),
    baseline,
  };
}

const withLink = (deps: Dependency[], link: Omit<Dependency, "id"> | null): Dependency[] =>
  link ? [...deps, { ...link, id: ops.newId("d") }] : deps;

const defaultUI: UIState = {
  scheduleView: "gantt",
  zoomPreset: "week",
  pxPerDay: ZOOM_PRESET_PX.week,
  density: "default",
  sidePanel: null,
  showBaseline: true,
  showCritical: true,
  showDependencies: true,
  showProgressLine: true,
  showFloat: true,
  showProgressLabel: true,
  showRemainingLabel: true,
  highlightDrivingPath: true,
  barLabel: "name",
  hiddenColumns: [...DEFAULT_HIDDEN_COLUMNS],
  pinnedColumn: "name",
  theme: "system",
  gridWidth: GRID_DEFAULT_WIDTH,
  viewRole: "pm",
  maskRates: false,
};

function mergeUI(partial: Partial<UIState> | Record<string, unknown> | undefined, theme: ThemePref): UIState {
  const raw = (partial ?? {}) as Partial<UIState> & { zoom?: ZoomPreset };
  const { zoom: _legacyZoom, ...rest } = raw;
  const zoomPreset = resolveZoomPreset(raw);
  const legacyColumns = raw.pinnedColumn === undefined && (!raw.hiddenColumns || raw.hiddenColumns.length === 0);
  return {
    ...defaultUI,
    ...rest,
    scheduleView: raw.scheduleView === "sheet" ? "sheet" : "gantt",
    zoomPreset,
    pxPerDay: resolvePxPerDay(raw),
    theme,
    hiddenColumns: legacyColumns ? [...DEFAULT_HIDDEN_COLUMNS] : (Array.isArray(raw.hiddenColumns) ? raw.hiddenColumns : defaultUI.hiddenColumns),
    pinnedColumn: isPinBoundary(raw.pinnedColumn) ? raw.pinnedColumn : defaultUI.pinnedColumn,
    // 패널 열림은 세션 안에서의 조작만 유지한다. 다시 들어오면 항상 닫힘.
    sidePanel: null,
  };
}

type EditorSlice = Pick<
  State,
  | "activeId"
  | "project"
  | "derived"
  | "past"
  | "pastKeepBody"
  | "future"
  | "futureKeepBody"
  | "lastImpact"
  | "changeLog"
  | "checkpoints"
  | "reports"
  | "selection"
  | "focusId"
  | "collapsed"
  | "ui"
  | "saveState"
  | "lastError"
  | "scrollTo"
  | "fitRequest"
  | "executionFocusId"
  | "executionFocusIsNew"
  | "executionFocusTab"
  | "executionFocusCommentId"
  | "executionReturnRecordId"
  | "executionSelection"
  | "recordFocusId"
  | "recordFocusIsNew"
  | "recordFocusTab"
  | "recordFocusCommentId"
  | "schedulePeekId"
  | "lastTab"
>;

/** State patch that hands the sheet back to the record the Task sheet was opened from (if it still exists). */
function returnToRecord(s: State): Partial<State> {
  const rid = s.executionReturnRecordId;
  if (!rid) return {};
  const alive = s.project.records.some((r) => r.id === rid);
  return {
    executionReturnRecordId: null,
    ...(alive ? { recordFocusId: rid, recordFocusIsNew: false, recordFocusTab: "details" as const, recordFocusCommentId: null } : {}),
  };
}

function emptyEditor(theme: ThemePref): EditorSlice {
  const project = createEmptyProject("—", todayISO());
  return {
    activeId: null,
    project,
    derived: derive(project),
    past: [],
    pastKeepBody: [],
    future: [],
    futureKeepBody: [],
    lastImpact: null,
    changeLog: [],
    checkpoints: [],
    reports: [],
    selection: [],
    focusId: null,
    collapsed: new Set(),
    ui: { ...defaultUI, theme },
    saveState: "saved",
    lastError: null,
    scrollTo: null,
    fitRequest: null,
    executionFocusId: null,
    executionFocusIsNew: false,
    executionFocusTab: "details",
    executionFocusCommentId: null,
    executionReturnRecordId: null,
    executionSelection: [],
    recordFocusId: null,
    recordFocusIsNew: false,
    recordFocusTab: "details",
    recordFocusCommentId: null,
    schedulePeekId: null,
    lastTab: "schedule",
  };
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
function schedulePersist(get: () => State & Actions, set: (p: Partial<State>) => void) {
  if (saveTimer) clearTimeout(saveTimer);
  set({ saveState: "dirty" });
  saveTimer = setTimeout(() => {
    const s = get();
    if (!s.activeId) {
      set({ saveState: "saved" });
      return;
    }
    try {
      useCatalogStore.getState().saveProject(s.project, {
        checkpoints: s.checkpoints,
        reports: s.reports,
        collapsed: [...s.collapsed],
        ui: s.ui,
        lastTab: s.lastTab,
      });
      set({ saveState: "saved" });
    } catch {
      set({ saveState: "dirty" });
    }
  }, 250);
}

/**
 * 편집기에 대기 중인 저장(250ms 디바운스)을 바로 카탈로그로 넘기고, 카탈로그도 바로 localStorage에 쓴다.
 * 창을 닫거나 앱을 끝내기 직전에 부른다(§4.4, ADV-C01). 실패하면 던진다.
 */
export function flushPendingSaves(): void {
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
    const s = useProjectStore.getState();
    if (s.activeId) {
      useCatalogStore.getState().saveProject(s.project, {
        checkpoints: s.checkpoints,
        reports: s.reports,
        collapsed: [...s.collapsed],
        ui: s.ui,
        lastTab: s.lastTab,
      });
    }
    useProjectStore.setState({ saveState: "saved" });
  }
  saveCatalogImmediately();
}

function editorFromCatalog(id: string): EditorSlice | null {
  const cat = useCatalogStore.getState();
  const project = cat.projects[id];
  if (!project) return null;
  const chrome = cat.byProject[id];
  const clone = normalizeProjectResources(xops.normalizeExecution(structuredClone(project)) as Project);
  return {
    executionFocusId: null,
    executionFocusIsNew: false,
    executionFocusTab: "details",
    executionFocusCommentId: null,
    executionReturnRecordId: null,
    executionSelection: [],
    recordFocusId: null,
    recordFocusIsNew: false,
    recordFocusTab: "details",
    recordFocusCommentId: null,
    schedulePeekId: null,
    lastTab: chrome?.lastTab ?? "schedule",
    activeId: id,
    project: clone,
    derived: derive(clone),
    past: [],
    pastKeepBody: [],
    future: [],
    futureKeepBody: [],
    lastImpact: null,
    changeLog: [],
    checkpoints: structuredClone((chrome?.checkpoints ?? []) as Checkpoint[]),
    reports: structuredClone(chrome?.reports ?? []),
    selection: [],
    focusId: null,
    collapsed: new Set(chrome?.collapsed ?? []),
    ui: mergeUI(chrome?.ui as Partial<UIState> | undefined, cat.theme),
    saveState: "saved",
    lastError: null,
    scrollTo: null,
    fitRequest: null,
  };
}

function retroWritable(project: Pick<Project, "lifecycle">): boolean {
  return project.lifecycle !== "archived" && project.lifecycle !== "trashed";
}

function withRetrospective(project: Project, retrospective: Project["retrospective"]): Project | null {
  const next = normalizeRetrospective(retrospective);
  const { retrospective: _drop, ...rest } = project;
  return next ? { ...rest, retrospective: next } : rest;
}

export const useProjectStore = create<State & Actions>()((set, get) => {
  const cat = useCatalogStore.getState();
  const bootId = cat.activeProjectId && cat.projects[cat.activeProjectId] ? cat.activeProjectId : null;
  const initial = bootId ? editorFromCatalog(bootId)! : emptyEditor(cat.theme);

  const persist = () => schedulePersist(get, set);

  const commit: Actions["commit"] = (label, fn, opts) => {
    const s = get();
    const produced = fn(structuredClone(s.project));
    if (!produced) return false;
    const next = ops.releaseAutoConstraints(produced);
    next.updatedAt = new Date().toISOString();
    next.tasks = ops.normalizeOrder(next.tasks);
    const derived = derive(next);
    const impact = diffSchedules(next, s.derived.schedule, derived.schedule, label, derived.calendar);
    const entry: ChangeLogEntry = {
      id: ops.newId("c"),
      at: next.updatedAt,
      label,
      taskIds: opts?.taskIds ?? impact.changedTaskIds.slice(0, 20),
      finishDeltaDays: impact.finishDeltaDays,
    };
    set({
      project: next,
      derived,
      ...historyPush(s, opts?.keepBody !== false),
      lastImpact: impact,
      changeLog: [entry, ...s.changeLog].slice(0, 500),
      lastError: null,
    });
    persist();
    return true;
  };

  const commitPlanner: Actions["commitPlanner"] = (_label, fn, opts) => {
    const s = get();
    const next = fn(structuredClone(s.project));
    if (!next) return false;
    next.updatedAt = new Date().toISOString();
    set({
      project: next,
      ...historyPush(s, opts?.keepBody !== false),
      lastError: null,
    });
    persist();
    return true;
  };

  const actor = () => useCatalogStore.getState().profile.displayName;
  const xtask = (id: string) => get().project.executionTasks.find((t) => t.id === id);
  /** apply a per-task transform as one undoable step */
  const patchX = (id: string, label: string, fn: (t: ExecutionTask) => ExecutionTask) =>
    commitPlanner(label, (p) => {
      const cur = p.executionTasks.find((t) => t.id === id);
      if (!cur) return null;
      const now = new Date().toISOString();
      return { ...p, executionTasks: p.executionTasks.map((t) => (t.id === id ? { ...fn(t), updatedAt: now } : t)) };
    });
  /** Hard Lock: a task with an open gate cannot be moved to "done" by any path. */
  const lockPatch = (t: ExecutionTask, patch: Partial<ExecutionTask>): Partial<ExecutionTask> => {
    if (patch.status !== "done" || t.status === "done" || xops.canCompleteExecutionTask(t).allowed) return patch;
    const { status: _blocked, ...rest } = patch;
    return rest;
  };
  const patchGate = (taskId: string, gateId: string, label: string, fn: (g: GateTag) => GateTag) =>
    commitPlanner(label, (p) => {
      const cur = p.executionTasks.find((t) => t.id === taskId);
      if (!cur || !xops.gateTagsOf(cur).some((g) => g.id === gateId)) return null;
      const now = new Date().toISOString();
      return {
        ...p,
        executionTasks: p.executionTasks.map((t) => {
          if (t.id !== taskId) return t;
          const next = xops.updateGateTag(t, gateId, fn, now);
          const name = next.gateTags?.find((g) => g.id === gateId)?.name ?? "게이트";
          let out = xops.logActivity(next, actor(), `[${name}] ${label}`, now);
          if (out.status !== t.status) out = xops.logActivity(out, actor(), `상태 → ${out.status ? xops.STATUS_LABEL[out.status] : "미정"} (게이트)`, now);
          return out;
        }),
      };
    });
  const patchRecord = (id: string, label: string, fn: (r: ProjectRecord) => ProjectRecord) =>
    commitPlanner(label, (p) => {
      const cur = p.records.find((r) => r.id === id);
      if (!cur) return null;
      const next = fn(cur);
      if (next === cur) return null;
      const now = new Date().toISOString();
      return { ...p, records: p.records.map((r) => (r.id === id ? { ...next, updatedAt: now } : r)) };
    });

  return {
    ...initial,
    previewPlanRole: null,
    previewWorkspaceAdmin: false,
    previewSelfResourceId: null,
    previewGuest: false,
    previewGuestPlanId: null,
    previewWorkspaceMember: false,
    dialog: null,
    pendingDeleteTaskIds: null,

    commit,
    commitPlanner,
    undo: () => {
      const s = get();
      const prev = s.past.at(-1);
      if (!prev) return;
      const keep = s.pastKeepBody.at(-1) !== false;
      const project = keep ? dops.restoreKeepingBodies(prev, s.project, dops.catalogOf(prev, wbsOf(derive(prev).schedule)), actor()) : prev;
      const derived = derive(project);
      const pastKeep = s.pastKeepBody.length === s.past.length ? s.pastKeepBody : s.past.map(() => true);
      set({
        project,
        derived,
        past: s.past.slice(0, -1),
        pastKeepBody: pastKeep.slice(0, -1),
        future: [s.project, ...s.future],
        futureKeepBody: [keep, ...s.futureKeepBody],
        lastImpact: diffSchedules(project, s.derived.schedule, derived.schedule, "실행 취소", derived.calendar),
      });
      persist();
    },
    redo: () => {
      const s = get();
      const next = s.future[0];
      if (!next) return;
      const keep = s.futureKeepBody[0] !== false;
      const project = keep ? dops.restoreKeepingBodies(next, s.project, dops.catalogOf(next, wbsOf(derive(next).schedule)), actor()) : next;
      const derived = derive(project);
      const pastKeep = s.pastKeepBody.length === s.past.length ? s.pastKeepBody : s.past.map(() => true);
      set({
        project,
        derived,
        past: [...s.past, s.project],
        pastKeepBody: [...pastKeep, keep],
        future: s.future.slice(1),
        futureKeepBody: s.futureKeepBody.slice(1),
        lastImpact: diffSchedules(project, s.derived.schedule, derived.schedule, "다시 실행", derived.calendar),
      });
      persist();
    },
    select: (ids, focus) => set({ selection: ids, focusId: focus === undefined ? (ids[0] ?? null) : focus }),
    toggleSelect: (id) => {
      const s = get();
      const has = s.selection.includes(id);
      set({ selection: has ? s.selection.filter((x) => x !== id) : [...s.selection, id], focusId: id });
    },
    toggleCollapse: (id) => {
      const c = new Set(get().collapsed);
      if (c.has(id)) c.delete(id);
      else c.add(id);
      set({ collapsed: c });
      persist();
    },
    setCollapsedAll: (collapsed) => {
      const s = get();
      const c = new Set<string>();
      if (collapsed) for (const [id, t] of s.derived.schedule.tasks) if (t.isSummary) c.add(id);
      set({ collapsed: c });
      persist();
    },
    setUI: (patch) => {
      const next = { ...get().ui, ...patch };
      if (patch.pxPerDay !== undefined) next.pxPerDay = clampPxPerDay(patch.pxPerDay);
      set({ ui: next });
      if (patch.theme) useCatalogStore.getState().setTheme(patch.theme);
      persist();
    },
    clearError: () => set({ lastError: null }),
    scrollToDate: (date, align = "third") => set({ scrollTo: { date, seq: (get().scrollTo?.seq ?? 0) + 1, align } }),
    setZoomPreset: (preset) => {
      const s = get();
      set({
        ui: { ...s.ui, zoomPreset: preset, pxPerDay: ZOOM_PRESET_PX[preset] },
        scrollTo: { date: todayISO(), seq: (s.scrollTo?.seq ?? 0) + 1, align: "center" },
      });
      persist();
    },
    nudgeZoom: (direction) => {
      set({ ui: { ...get().ui, pxPerDay: nudgePxPerDay(get().ui.pxPerDay, direction) } });
      persist();
    },
    setZoomPercent: (percent) => {
      const ui = get().ui;
      set({ ui: { ...ui, pxPerDay: pxPerDayFromPercent(ui.zoomPreset, percent) } });
      persist();
    },
    requestFit: (mode = "project") => {
      const selection = get().selection;
      const resolved = mode === "selection" && selection.length === 0 ? "project" : mode;
      set({ fitRequest: { mode: resolved, seq: (get().fitRequest?.seq ?? 0) + 1 } });
    },
    openDialog: (dialog) => set({ dialog }),

    updateTask: (id, patch, label) => {
      const t = get().project.tasks.find((x) => x.id === id);
      const name = t?.name || "작업";
      const keys = Object.keys(patch).join(", ");
      commit(label ?? `${name}: ${keys} 변경`, (p) => ({ ...p, tasks: ops.updateTask(p.tasks, id, patch) }), { taskIds: [id] });
    },
    updateTasks: (patches, label) => {
      if (!patches.length) return;
      commit(
        label,
        (p) => {
          let tasks = p.tasks;
          for (const { id, patch } of patches) tasks = ops.updateTask(tasks, id, patch);
          return { ...p, tasks };
        },
        { taskIds: patches.map((x) => x.id) },
      );
    },
    addSibling: (afterId, partial) => {
      const s = get();
      const { task, link } = ops.planInsert(s.project, s.derived.schedule, { kind: "sibling", afterId }, partial);
      commit(
        task.duration === 0 ? "마일스톤 추가" : "작업 추가",
        (p) => ({ ...p, tasks: ops.addSibling(p.tasks, afterId, task), dependencies: withLink(p.dependencies, link) }),
        { taskIds: [task.id] },
      );
      set({ selection: [task.id], focusId: task.id });
      return task.id;
    },
    addChild: (parentId, partial) => {
      const s = get();
      const { task, link } = ops.planInsert(s.project, s.derived.schedule, { kind: "child", parentId }, partial);
      commit(
        "하위 작업 추가",
        (p) => ({ ...p, tasks: ops.addChild(p.tasks, parentId, task), dependencies: withLink(p.dependencies, link) }),
        { taskIds: [task.id] },
      );
      const c = new Set(get().collapsed);
      c.delete(parentId);
      set({ selection: [task.id], focusId: task.id, collapsed: c });
      return task.id;
    },
    groupSelection: (name) => {
      const s = get();
      if (!s.selection.length) return null;
      const summary = ops.makeTask({ name: name?.trim() || "새 단계", duration: 1 });
      const ok = commit(
        `${s.selection.length}개 작업을 '${summary.name}'으로 묶기`,
        (p) => {
          const tasks = ops.groupTasks(p.tasks, s.selection, summary);
          return tasks ? { ...p, tasks } : null;
        },
        { taskIds: [summary.id, ...s.selection] },
      );
      if (!ok) {
        set({ lastError: "같은 상위 작업 아래에 있는 작업들만 하나로 묶을 수 있습니다." });
        return null;
      }
      set({ selection: [summary.id], focusId: summary.id });
      return summary.id;
    },
    requestDeleteTasks: (ids) => {
      if (!ids.length) return;
      set({ pendingDeleteTaskIds: [...ids] });
    },
    cancelDeleteTasks: () => set({ pendingDeleteTaskIds: null }),
    deleteTasks: (ids) => {
      if (!ids.length) return;
      set({ pendingDeleteTaskIds: null });
      const s = get();
      const order = s.derived.schedule.order;
      const firstIdx = Math.min(...ids.map((id) => order.indexOf(id)).filter((i) => i >= 0));
      const wbs = wbsOf(s.derived.schedule);
      commit(`${ids.length}개 작업 삭제`, (p) => {
        const marked = dops.tombstoneTargets(p, "scheduleTask", ids, dops.catalogOf(p, wbs));
        const r = ops.removeTasks(marked, ids);
        if (r.tasks.length === 0) r.tasks = [ops.makeTask({ name: "첫 작업", duration: 5 })];
        // execution tasks and records survive; only their scope links are dropped (no cascade)
        const executionTasks = xops.pruneLinks(marked.executionTasks, r.tasks.map((t) => t.id));
        const records = rops.pruneRecordLinks(marked.records, { taskIds: r.tasks.map((t) => t.id) });
        return { ...marked, ...r, executionTasks, records };
      }, { taskIds: ids });
      const newOrder = get().derived.schedule.order;
      const nextFocus = newOrder[Math.min(firstIdx, newOrder.length - 1)] ?? null;
      set({ selection: nextFocus ? [nextFocus] : [], focusId: nextFocus });
    },
    indent: (ids) => {
      const order = get().derived.schedule.order;
      const sorted = [...ids].sort((a, b) => order.indexOf(a) - order.indexOf(b));
      commit("들여쓰기", (p) => {
        let tasks = p.tasks;
        for (const id of sorted) tasks = ops.indent(tasks, id);
        return { ...p, tasks };
      }, { taskIds: ids });
    },
    outdent: (ids) => {
      const order = get().derived.schedule.order;
      const sorted = [...ids].sort((a, b) => order.indexOf(b) - order.indexOf(a));
      commit("내어쓰기", (p) => {
        let tasks = p.tasks;
        for (const id of sorted) tasks = ops.outdent(tasks, id);
        return { ...p, tasks };
      }, { taskIds: ids });
    },
    moveUp: (id) => commit("위로 이동", (p) => ({ ...p, tasks: ops.moveUp(p.tasks, id) }), { taskIds: [id] }),
    moveDown: (id) => commit("아래로 이동", (p) => ({ ...p, tasks: ops.moveDown(p.tasks, id) }), { taskIds: [id] }),

    addDependency: (dep) => {
      const s = get();
      if (s.project.dependencies.some((d) => d.predecessorId === dep.predecessorId && d.successorId === dep.successorId)) {
        set({ lastError: "이미 같은 두 작업 사이에 관계가 있습니다." });
        return false;
      }
      if (wouldCreateCycle(s.project, dep)) {
        set({ lastError: "이 관계를 추가하면 순환이 생기거나 요약 작업과 하위 작업이 연결됩니다. 거절했습니다." });
        return false;
      }
      return commit("관계 추가", (p) => ({ ...p, dependencies: [...p.dependencies, { ...dep, id: ops.newId("d") }] }), {
        taskIds: [dep.successorId],
      });
    },
    updateDependency: (id, patch) => {
      const s = get();
      const cur = s.project.dependencies.find((d) => d.id === id);
      if (!cur) return;
      const next = { ...cur, ...patch };
      const without = { ...s.project, dependencies: s.project.dependencies.filter((d) => d.id !== id) };
      if (wouldCreateCycle(without, next)) {
        set({ lastError: "변경하면 순환이 생깁니다. 거절했습니다." });
        return;
      }
      commit("관계 변경", (p) => ({ ...p, dependencies: p.dependencies.map((d) => (d.id === id ? next : d)) }), {
        taskIds: [next.successorId],
      });
    },
    removeDependency: (id) => {
      const dep = get().project.dependencies.find((d) => d.id === id);
      commit("관계 삭제", (p) => ({ ...p, dependencies: p.dependencies.filter((d) => d.id !== id) }), {
        taskIds: dep ? [dep.successorId] : [],
      });
    },
    setPredecessorsFromText: (taskId, text) => {
      const s = get();
      const parsed = ops.parsePredecessors(s.project, taskId, text);
      if (!parsed) {
        set({ lastError: "선행 형식이 올바르지 않습니다. 예: 3, 5SS+2, 7FF-1" });
        return false;
      }
      const base: Project = { ...s.project, dependencies: s.project.dependencies.filter((d) => d.successorId !== taskId) };
      const deps: Dependency[] = [];
      for (const d of parsed) {
        const probe = { ...base, dependencies: [...base.dependencies, ...deps] };
        if (wouldCreateCycle(probe, d)) {
          set({ lastError: "순환이 생기는 선행 관계가 있어 거절했습니다." });
          return false;
        }
        deps.push({ ...d, id: ops.newId("d") });
      }
      return commit("선행 관계 편집", (p) => ({
        ...p,
        dependencies: [...p.dependencies.filter((d) => d.successorId !== taskId), ...deps],
      }), { taskIds: [taskId] });
    },
    linkSelection: () => {
      const s = get();
      const order = s.derived.schedule.order;
      const ids = [...s.selection].sort((a, b) => order.indexOf(a) - order.indexOf(b));
      if (ids.length < 2) {
        set({ lastError: "연결할 작업을 2개 이상 선택하세요." });
        return;
      }
      for (let i = 0; i < ids.length - 1; i++) {
        const ok = get().addDependency({ predecessorId: ids[i]!, successorId: ids[i + 1]!, type: "FS", lag: 0 });
        if (!ok) return;
      }
    },

    setProject: (patch) => commit("플랜 설정 변경", (p) => ({ ...p, ...patch })),
    setLifecycle: (lifecycle) =>
      commit(lifecycle === "archived" ? "플랜 아카이브" : lifecycle === "done" ? "플랜 완료" : "플랜 복구", (p) => ({
        ...p,
        lifecycle: lifecycle === "active" ? undefined : lifecycle,
        ...(lifecycle === "done" ? { statusOverride: "done" as const } : {}),
      })),
    setRetrospectiveSummary: (summary) => {
      if (!retroWritable(get().project)) return;
      commitPlanner("회고 요약", (p) => withRetrospective(p, { ...(p.retrospective ?? { cards: [] }), summary }));
    },
    addRetrospectiveCard: (draft) => {
      if (!retroWritable(get().project)) return;
      const actor = useCatalogStore.getState().profile.displayName;
      commitPlanner("회고 카드", (p) => {
        const cards = addKptCard(p.retrospective?.cards ?? [], {
          id: ops.newId("kpt"),
          column: draft.column,
          body: draft.body,
          authorId: actor,
          authorName: actor,
          ...(draft.anonymous ? { anonymous: true } : {}),
          createdAt: new Date().toISOString(),
        });
        if (cards.length === (p.retrospective?.cards.length ?? 0)) return null;
        return withRetrospective(p, { ...(p.retrospective ?? { cards: [] }), cards });
      });
    },
    upvoteRetrospectiveCard: (cardId) => {
      if (!retroWritable(get().project)) return;
      const actor = useCatalogStore.getState().profile.displayName;
      commitPlanner("회고 투표", (p) => {
        const current = p.retrospective?.cards ?? [];
        const cards = upvoteKpt(current, cardId, actor);
        if (cards === current) return null;
        return withRetrospective(p, { ...(p.retrospective ?? { cards: [] }), cards: [...cards] });
      });
    },
    setCalendar: (def) => commit("캘린더 변경", (p) => ({ ...p, calendar: def })),
    saveBaseline: (name, note) => {
      const s = get();
      const next = prepareBaselineSave(s.project.baselines.length, name, note);
      if (!next) {
        set({ lastError: BASELINE_LIMIT_ERROR });
        return;
      }
      commit(`기준선 저장 (${next.name})`, (p) => {
        const bl = createBaseline(p, s.derived.schedule, next.name, next.note);
        return { ...p, baselines: [...p.baselines, bl], activeBaselineId: bl.id };
      });
    },
    setActiveBaseline: (id) => {
      if (get().project.activeBaselineId === id) return;
      commit("기준선 선택", (p) => ({ ...p, activeBaselineId: id }));
    },
    renameBaseline: (id, name, note) => {
      const current = get().project.baselines.find((b) => b.id === id);
      if (!current) return;
      const next = prepareBaselineRename(current.name, current.note, name, note);
      if (!next) return;
      const label = next.name !== current.name ? `기준선 이름 변경 (${next.name})` : `기준선 메모 변경 (${next.name})`;
      commit(label, (p) => ({
        ...p,
        baselines: p.baselines.map((b) => {
          if (b.id !== id) return b;
          const renamed: Baseline = { ...b, name: next.name };
          if (next.note) renamed.note = next.note;
          else delete renamed.note;
          return renamed;
        }),
      }));
    },
    deleteBaseline: (id) =>
      commit("기준선 삭제", (p) => ({
        ...p,
        baselines: p.baselines.filter((b) => b.id !== id),
        activeBaselineId: p.activeBaselineId === id ? (p.baselines.filter((b) => b.id !== id).at(-1)?.id ?? null) : p.activeBaselineId,
      })),

    saveCheckpoint: (name) => {
      const s = get();
      const cp: Checkpoint = {
        id: ops.newId("cp"),
        name: name?.trim() || `체크포인트 ${s.checkpoints.length + 1}`,
        createdAt: new Date().toISOString(),
        project: structuredClone(s.project),
      };
      set({ checkpoints: [cp, ...s.checkpoints].slice(0, 20) });
      persist();
    },
    restoreCheckpoint: (id, keepProgress) => {
      const s = get();
      const cp = s.checkpoints.find((c) => c.id === id);
      if (!cp) return;
      commit(`체크포인트 복원 (${cp.name})`, (p) => {
        const restored = structuredClone(cp.project);
        if (keepProgress) {
          const cur = new Map(p.tasks.map((t) => [t.id, t]));
          restored.tasks = restored.tasks.map((t) => {
            const c = cur.get(t.id);
            return c ? { ...t, percentComplete: c.percentComplete } : t;
          });
        }
        // keep baselines created after the checkpoint
        const known = new Set(restored.baselines.map((b) => b.id));
        restored.baselines = [...restored.baselines, ...p.baselines.filter((b) => !known.has(b.id))];
        restored.activeBaselineId = p.activeBaselineId ?? restored.activeBaselineId;
        return restored;
      });
    },
    deleteCheckpoint: (id) => {
      set({ checkpoints: get().checkpoints.filter((c) => c.id !== id) });
      persist();
    },

    closeReportPeriod: (name, note) => {
      const s = get();
      const resources = useCatalogStore.getState().resources;
      const statusDate = s.project.statusDate;
      const closedAt = new Date().toISOString();
      const existingDraft = s.project.weeklyDrafts?.[statusDate];
      const existingFrozen = findWeeklySnapshot(s.project, statusDate, statusDate);

      let nextProject = s.project;
      if (!existingFrozen || existingDraft?.isRevision) {
        const draftBase = existingDraft ?? seedWeeklyDraft(s.project, resources, statusDate, actor());
        const draft = name?.trim() ? { ...draftBase, title: name.trim() } : draftBase;
        const report = buildReportFromDraft(draft, s.project, closedAt);
        const frozenRes = freezeWeeklyReport(s.project, {
          id: ops.newId("wsnap"),
          weekId: statusDate,
          statusDate,
          periodLabel: draft.periodLabel,
          report,
          closedAt,
          revision: !!draft.isRevision,
        });
        const nextDrafts = { ...(frozenRes.project.weeklyDrafts ?? {}) };
        delete nextDrafts[statusDate];
        nextProject = {
          ...frozenRes.project,
          weeklyDrafts: Object.keys(nextDrafts).length > 0 ? nextDrafts : undefined,
          updatedAt: closedAt,
        };
      }

      const periodName = name?.trim() || existingDraft?.title?.trim() || suggestPeriodName(statusDate);
      const period = makeReportPeriod(nextProject, resources, {
        id: ops.newId("rp"),
        name: periodName,
        note,
        closedAt,
      });
      set({ project: nextProject, reports: [period, ...s.reports].slice(0, REPORT_PERIOD_LIMIT) });
      persist();
      return period.id;
    },
    promoteCheckpointToReport: (checkpointId, name) => {
      const s = get();
      const cp = s.checkpoints.find((c) => c.id === checkpointId);
      if (!cp) return null;
      const period = makeReportPeriod(cp.project, useCatalogStore.getState().resources, {
        id: ops.newId("rp"),
        name: name?.trim() || cp.name,
        closedAt: cp.createdAt,
        source: "checkpoint",
      });
      const reports = [period, ...s.reports].sort((a, b) => (a.closedAt < b.closedAt ? 1 : -1)).slice(0, REPORT_PERIOD_LIMIT);
      set({ reports });
      persist();
      return period.id;
    },
    deleteReportPeriod: (id) => {
      set({ reports: get().reports.filter((r) => r.id !== id) });
      persist();
    },
    createDemoReports: () => {
      const s = get();
      const resources = useCatalogStore.getState().resources;
      const { project, periods } = buildDemoHistory(s.project, resources);
      if (!s.project.baselines.length && project.baselines[0]) {
        const bl = project.baselines[0];
        commit(`기준선 저장 (${bl.name})`, (p) => ({ ...p, baselines: [bl], activeBaselineId: bl.id }));
      }
      let nextProject = get().project;
      const sortedPeriods = [...periods].sort((a, b) => (a.metrics.statusDate < b.metrics.statusDate ? -1 : 1));
      for (const period of sortedPeriods) {
        const pDate = period.metrics.statusDate as ISODate;
        if (findWeeklySnapshot(nextProject, pDate, pDate)) continue;
        const draft = seedWeeklyDraft({ ...period.project, weeklySnapshots: nextProject.weeklySnapshots }, resources, pDate, actor());
        const report = buildReportFromDraft(draft, nextProject, period.closedAt);
        const res = freezeWeeklyReport(nextProject, {
          id: `wsnap_demo_${period.id}`,
          weekId: pDate,
          statusDate: pDate,
          periodLabel: draft.periodLabel,
          report,
          closedAt: period.closedAt,
        });
        nextProject = res.project;
      }
      const kept = get().reports.filter((r) => r.source !== "demo");
      set({
        project: nextProject,
        reports: [...kept, ...periods].sort((a, b) => (a.closedAt < b.closedAt ? 1 : -1)).slice(0, REPORT_PERIOD_LIMIT),
      });
      persist();
    },
    clearDemoReports: () => {
      const s = get();
      const nextSnapshots = (s.project.weeklySnapshots ?? []).filter((snap) => !snap.id.startsWith("wsnap_demo_"));
      set({
        project: { ...s.project, weeklySnapshots: nextSnapshots },
        reports: s.reports.filter((r) => r.source !== "demo"),
      });
      persist();
    },

    ensureWeeklyDraft: (statusDate) => {
      const s = get();
      const targetDate = statusDate ?? s.project.statusDate;
      const existingDraft = s.project.weeklyDrafts?.[targetDate];
      if (existingDraft) return existingDraft;
      // 같은 기준일의 마감본이 있으면 초안을 새로 만들지 않는다. 정정본만 만든다.
      const existingClosed = findWeeklySnapshot(s.project, targetDate, targetDate);
      if (existingClosed) return null;

      const resources = useCatalogStore.getState().resources;
      const seeded = seedWeeklyDraft(s.project, resources, targetDate, actor());
      const nextProject: Project = {
        ...s.project,
        weeklyDrafts: {
          ...(s.project.weeklyDrafts ?? {}),
          [targetDate]: seeded,
        },
      };
      set({ project: nextProject });
      persist();
      return seeded;
    },

    updateWeeklyDraft: (statusDate, updater) => {
      const s = get();
      const resources = useCatalogStore.getState().resources;
      const existing = s.project.weeklyDrafts?.[statusDate];
      const base =
        existing ??
        (findWeeklySnapshot(s.project, statusDate, statusDate)
          ? null
          : seedWeeklyDraft(s.project, resources, statusDate, actor()));
      if (!base) return;
      const updated = updater(structuredClone(base));
      const nextProject: Project = {
        ...s.project,
        weeklyDrafts: {
          ...(s.project.weeklyDrafts ?? {}),
          [statusDate]: updated,
        },
        updatedAt: new Date().toISOString(),
      };
      set({ project: nextProject });
      persist();
    },

    setWeeklySectionDefaults: (patch) => {
      const s = get();
      const current = s.project.weeklySectionDefaults ?? DEFAULT_WEEKLY_SECTIONS;
      const nextDefaults: WeeklySectionVisibility = {
        ...current,
        ...patch,
      };
      set({
        project: {
          ...s.project,
          weeklySectionDefaults: nextDefaults,
          updatedAt: new Date().toISOString(),
        },
      });
      persist();
    },

    closeWeeklyReport: (statusDate) => {
      const s = get();
      const targetDate = statusDate ?? s.project.statusDate;
      const resources = useCatalogStore.getState().resources;
      const existingDraft = s.project.weeklyDrafts?.[targetDate];
      const existingClosed = findWeeklySnapshot(s.project, targetDate, targetDate);

      if (existingClosed && !existingDraft?.isRevision) {
        return existingClosed;
      }

      const draft = existingDraft ?? seedWeeklyDraft(s.project, resources, targetDate, actor());
      const closedAt = new Date().toISOString();
      const report = buildReportFromDraft(draft, s.project, closedAt);
      const frozenRes = freezeWeeklyReport(s.project, {
        id: ops.newId("wsnap"),
        weekId: targetDate,
        statusDate: targetDate,
        periodLabel: draft.periodLabel,
        report,
        closedAt,
        revision: !!draft.isRevision,
      });

      const nextDrafts = { ...(frozenRes.project.weeklyDrafts ?? {}) };
      delete nextDrafts[targetDate];
      const nextProject: Project = {
        ...frozenRes.project,
        weeklyDrafts: Object.keys(nextDrafts).length > 0 ? nextDrafts : undefined,
        updatedAt: closedAt,
      };

      // 리포트 탭의 기간 마감과 주간 보고 마감은 같은 동작이다.
      const hasPeriod = s.reports.some((r) => r.metrics.statusDate === targetDate);
      let nextReports = s.reports;
      if (!hasPeriod) {
        const planForPeriod = targetDate === nextProject.statusDate ? nextProject : { ...nextProject, statusDate: targetDate };
        const period = makeReportPeriod(planForPeriod, resources, {
          id: ops.newId("rp"),
          name: draft.title.trim() || suggestPeriodName(targetDate),
          closedAt,
        });
        nextReports = [period, ...s.reports].slice(0, REPORT_PERIOD_LIMIT);
      } else if (draft.isRevision) {
        nextReports = s.reports.map((r) =>
          r.metrics.statusDate === targetDate ? { ...r, name: draft.title.trim() || r.name } : r,
        );
      }

      set({ project: nextProject, reports: nextReports });
      persist();
      return frozenRes.snapshot;
    },

    openWeeklyRevision: (statusDate) => {
      const s = get();
      const existingClosed = findWeeklySnapshot(s.project, statusDate, statusDate);
      if (!existingClosed) return null;
      const resources = useCatalogStore.getState().resources;
      const revDraft = createRevisionDraftFromSnapshot(existingClosed, s.project, resources);
      if (!revDraft) return null;
      const nextProject: Project = {
        ...s.project,
        weeklyDrafts: {
          ...(s.project.weeklyDrafts ?? {}),
          [statusDate]: revDraft,
        },
        updatedAt: new Date().toISOString(),
      };
      set({ project: nextProject });
      persist();
      return revDraft;
    },

    discardWeeklyDraft: (statusDate) => {
      const s = get();
      if (!s.project.weeklyDrafts?.[statusDate]) return;
      const nextDrafts = { ...s.project.weeklyDrafts };
      delete nextDrafts[statusDate];
      set({
        project: {
          ...s.project,
          weeklyDrafts: Object.keys(nextDrafts).length > 0 ? nextDrafts : undefined,
          updatedAt: new Date().toISOString(),
        },
      });
      persist();
    },

    loadSample: () => {
      const id = useCatalogStore.getState().createProject({ name: "제조사 ERP 구축", template: "sample" });
      get().openProject(id);
      return id;
    },
    loadDemoCatalog: () => {
      const id = useCatalogStore.getState().loadDemoCatalog();
      get().openProject(id);
      return id;
    },
    newProject: () => {
      const id = useCatalogStore.getState().createProject({ name: "새 플랜", start: todayISO(), template: "empty" });
      get().openProject(id);
      return id;
    },
    importProject: (json) => {
      try {
        const data = JSON.parse(json) as Project;
        if (!Array.isArray(data.tasks) || !data.start) throw new Error("invalid");
        const project: Project = xops.normalizeExecution({
          ...createEmptyProject(),
          ...data,
          baselines: data.baselines ?? [],
          dependencies: data.dependencies ?? [],
          criticalThreshold: data.criticalThreshold ?? 0,
        }) as Project;
        const id = useCatalogStore.getState().importProjectData(project);
        get().openProject(id);
        return true;
      } catch {
        set({ lastError: "JSON을 읽을 수 없습니다." });
        return false;
      }
    },
    exportProject: () => JSON.stringify(get().project, null, 2),
    openProject: (id) => {
      const next = editorFromCatalog(id);
      if (!next) return false;
      set({ ...next, previewPlanRole: null, previewWorkspaceAdmin: false, previewSelfResourceId: null, previewGuest: false, previewGuestPlanId: null, previewWorkspaceMember: false, dialog: get().dialog, pendingDeleteTaskIds: null });
      useCatalogStore.getState().setActiveProjectId(id);
      return true;
    },
    closeProject: () => {
      set({ ...emptyEditor(useCatalogStore.getState().theme), previewPlanRole: null, previewWorkspaceAdmin: false, previewSelfResourceId: null, previewGuest: false, previewGuestPlanId: null, previewWorkspaceMember: false, dialog: get().dialog, pendingDeleteTaskIds: null });
      useCatalogStore.getState().setActiveProjectId(null);
    },
    setLastTab: (tab) => {
      if (get().lastTab === tab) return;
      set({ lastTab: tab });
      persist();
    },
    resetEditorSession: () => {
      const s = get();
      set({
        selection: [],
        focusId: null,
        executionFocusId: null,
        executionFocusIsNew: false,
        executionFocusTab: "details",
        executionFocusCommentId: null,
        executionReturnRecordId: null,
        executionSelection: [],
        recordFocusId: null,
        recordFocusIsNew: false,
        recordFocusTab: "details",
        recordFocusCommentId: null,
        schedulePeekId: null,
        dialog: null,
        pendingDeleteTaskIds: null,
        lastImpact: null,
        lastError: null,
        scrollTo: null,
        lastTab: "schedule",
        ui: { ...s.ui, sidePanel: null },
      });
      persist();
    },

    /* ---------------------------------------------------------------- Planner execution tasks */

    addExecutionTask: (partial, opts) => {
      const s = get();
      const maxOrder = s.project.executionTasks.reduce((m, t) => Math.max(m, t.order), -1);
      const today = todayISO();
      const defaultDates = { start: today, due: addDays(today, 3) };
      let seed = partial;
      if (s.previewPlanRole === "contributor" && s.previewSelfResourceId && !partial?.assignees?.length) {
        const name = useCatalogStore.getState().resources[s.previewSelfResourceId]?.displayName;
        if (name) seed = { ...partial, assignees: [name] };
      }
      let task = xops.makeExecutionTask({ order: maxOrder + 1, ...defaultDates, ...seed });
      task = xops.logActivity(task, actor(), "생성", task.createdAt);
      commitPlanner("Task 추가", (p) => ({ ...p, executionTasks: [...p.executionTasks, task] }));
      if (opts?.open !== false) set({ executionFocusId: task.id, executionFocusIsNew: true, executionReturnRecordId: null, executionFocusTab: "details", executionFocusCommentId: null });
      return task.id;
    },
    updateExecutionTask: (id, patch, label) => {
      const cur = xtask(id);
      if (!cur) return;
      patch = lockPatch(cur, patch);
      if (!Object.keys(patch).length) return;
      const keys = Object.keys(patch) as Array<keyof ExecutionTask>;
      const message = label ?? describePatch(cur, patch, get().project);
      commitPlanner(`${cur.title || "Task"}: ${keys.join(", ")}`, (p) => {
        const now = new Date().toISOString();
        return {
          ...p,
          executionTasks: p.executionTasks.map((t) => {
            if (t.id !== id) return t;
            let next: ExecutionTask = { ...t, ...patch, updatedAt: now };
            if ("status" in patch) {
              if (next.status === "done") next.completedAt = next.completedAt ?? now;
              else delete next.completedAt;
            }
            if (message) next = xops.logActivity(next, actor(), message, now);
            return next;
          }),
        };
      });
    },
    updateExecutionTasks: (ids, patch, label) => {
      if (!ids.length) return;
      const set_ = new Set(ids);
      commitPlanner(label, (p) => {
        const now = new Date().toISOString();
        return {
          ...p,
          executionTasks: p.executionTasks.map((t) => {
            if (!set_.has(t.id)) return t;
            let next: ExecutionTask = { ...t, ...lockPatch(t, patch), updatedAt: now };
            if ("status" in patch) {
              if (next.status === "done") next.completedAt = next.completedAt ?? now;
              else delete next.completedAt;
            }
            next = xops.logActivity(next, actor(), label, now);
            return next;
          }),
        };
      });
    },
    setExecutionStatus: (id, status) => {
      const cur = xtask(id);
      if (!cur || cur.status === status) return;
      if (status === "done" && !xops.canCompleteExecutionTask(cur).allowed) return;
      commitPlanner(`상태 변경`, (p) => ({ ...p, executionTasks: xops.setStatus(p.executionTasks, id, status, actor()).list }));
    },
    deleteExecutionTasks: (ids) => {
      if (!ids.length) return;
      const wbs = wbsOf(get().derived.schedule);
      commitPlanner(`Task ${ids.length}개 삭제`, (p) => {
        const marked = dops.tombstoneTargets(p, "executionTask", ids, dops.catalogOf(p, wbs));
        const executionTasks = xops.removeExecutionTasks(marked.executionTasks, ids);
        const records = rops.pruneRecordLinks(marked.records, { executionTaskIds: executionTasks.map((t) => t.id) });
        return { ...marked, executionTasks, records };
      });
      const s = get();
      const closesSheet = !!s.executionFocusId && ids.includes(s.executionFocusId);
      set({
        ...(closesSheet ? { executionFocusId: null, executionFocusIsNew: false, executionFocusTab: "details", executionFocusCommentId: null, ...returnToRecord(s) } : {}),
        executionSelection: s.executionSelection.filter((x) => !ids.includes(x)),
      });
    },
    duplicateExecutionTask: (id) => {
      let newId: string | null = null;
      commitPlanner("Task 복제", (p) => {
        const r = xops.duplicateExecutionTask(p.executionTasks, id);
        newId = r.id;
        return r.id ? { ...p, executionTasks: r.list } : null;
      });
      if (newId) set({ executionFocusId: newId, executionFocusTab: "details", executionFocusCommentId: null });
      return newId;
    },
    moveExecutionTask: (id, patch, columnIds, beforeId) => {
      const cur = xtask(id);
      if (!cur) return;
      patch = lockPatch(cur, patch);
      const message = describePatch(cur, patch, get().project);
      commitPlanner("Task 이동", (p) => {
        const now = new Date().toISOString();
        let list = p.executionTasks.map((t) => {
          if (t.id !== id) return t;
          let next: ExecutionTask = { ...t, ...patch, updatedAt: now };
          if ("status" in patch) {
            if (next.status === "done") next.completedAt = next.completedAt ?? now;
            else delete next.completedAt;
          }
          if (message) next = xops.logActivity(next, actor(), message, now);
          return next;
        });
        list = xops.reorderWithin(list, id, columnIds.includes(id) ? columnIds : [...columnIds, id], beforeId, now);
        return { ...p, executionTasks: list };
      });
    },
    addChecklistItem: (id, text, extra) => patchX(id, "체크리스트 추가", (t) => xops.addChecklistItem(t, text, extra)),
    updateChecklistItem: (id, itemId, patch) => patchX(id, "체크리스트 변경", (t) => xops.updateChecklistItem(t, itemId, patch)),
    removeChecklistItem: (id, itemId) => patchX(id, "체크리스트 삭제", (t) => xops.removeChecklistItem(t, itemId)),
    convertChecklistToTask: (id, itemId) => {
      let createdId: string | null = null;
      commitPlanner("체크리스트 항목을 Task로 승격", (p) => {
        const { list, newTask } = xops.convertChecklistToTask(p.executionTasks, id, itemId);
        if (!newTask) return null;
        createdId = newTask.id;
        return { ...p, executionTasks: list };
      });
      return createdId;
    },
    addTaskComment: (id, body, options) => patchX(id, options?.parentId ? "대댓글" : "댓글", (t) => xops.addComment(t, actor(), body, options)),
    updateTaskComment: (id, commentId, body, options) => patchX(id, "댓글 수정", (t) => xops.updateComment(t, commentId, body, options)),
    deleteTaskComment: (id, commentId) => patchX(id, "댓글 삭제", (t) => xops.deleteComment(t, commentId)),
    addTaskAttachment: (id, title, url, extra) => patchX(id, "첨부 추가", (t) => xops.logActivity(xops.addAttachment(t, title, url, { ...extra, addedBy: extra?.addedBy || actor() }), actor(), `첨부 추가: ${title || url}`)),
    removeTaskAttachment: (id, attachmentId) => patchX(id, "첨부 삭제", (t) => xops.removeAttachment(t, attachmentId)),
    setExecutionFocus: (id, opts) => {
      const tab = opts?.commentId ? "comments" : (opts?.tab ?? "details");
      if (id) {
        set({
          executionFocusId: id,
          executionFocusIsNew: !!opts?.isNew,
          executionReturnRecordId: opts?.returnToRecordId ?? null,
          executionFocusTab: tab,
          executionFocusCommentId: opts?.commentId ?? null,
          recordFocusId: null,
          recordFocusIsNew: false,
          schedulePeekId: null,
        });
        return;
      }
      set({ executionFocusId: null, executionFocusIsNew: false, executionFocusTab: "details", executionFocusCommentId: null, ...returnToRecord(get()) });
    },
    setExecutionSelection: (ids) => set({ executionSelection: ids }),
    toggleExecutionSelection: (id) => {
      const cur = get().executionSelection;
      set({ executionSelection: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
    },

    /* ---------------------------------------------------------------- QA gate tags */

    addGateTag: (taskId, type, opts) =>
      patchX(taskId, "게이트 추가", (t) => {
        const gate = xops.makeGateTag(type, opts?.criteria ?? [], opts?.name);
        return xops.logActivity(xops.addGateTag(t, gate), actor(), `게이트 추가: [${gate.name}]`);
      }),
    removeGateTag: (taskId, gateId) => patchX(taskId, "게이트 삭제", (t) => xops.settleTaskByGates(xops.removeGateTag(t, gateId))),
    addGateChecklistItem: (taskId, gateId, text) => patchGate(taskId, gateId, "체크 항목 추가", (g) => xops.addGateChecklistItem(g, text)),
    removeGateChecklistItem: (taskId, gateId, itemId) => patchGate(taskId, gateId, "체크 항목 삭제", (g) => xops.removeGateChecklistItem(g, itemId)),
    toggleGateChecklistDev: (taskId, gateId, itemId) => patchGate(taskId, gateId, "Dev 체크 변경", (g) => xops.toggleGateChecklistDev(g, itemId)),
    requestGateReview: (taskId, gateId) => patchGate(taskId, gateId, "검수 요청", (g) => xops.requestGateReview(g)),
    setGateChecklistQaStatus: (taskId, gateId, itemId, status, reason) =>
      patchGate(taskId, gateId, "검수 판정", (g) => xops.setGateChecklistQaStatus(g, itemId, status, actor(), reason)),
    setGateVerdict: (taskId, gateId, verdict, reason) =>
      patchGate(taskId, gateId, verdict === "pass" ? "검수 통과" : "검수 반려", (g) => xops.setGateTagVerdict(g, verdict, actor(), reason)),

    /* ---------------------------------------------------------------- Intake */

    addIntake: (partial) => {
      let id = "";
      commitPlanner("요구사항 접수", (p) => {
        const r = iops.createIntakeItem(p.intakes ?? [], { author: actor(), ...partial });
        id = r.item.id;
        return { ...p, intakes: r.list };
      });
      return id;
    },
    updateIntake: (id, patch) => commitPlanner("요구사항 수정", (p) => ({ ...p, intakes: iops.updateIntakeItem(p.intakes ?? [], id, patch) })),
    deleteIntakes: (ids) => {
      if (!ids.length) return;
      commitPlanner(`요구사항 ${ids.length}건 삭제`, (p) => ({ ...p, intakes: iops.removeIntakeItems(p.intakes ?? [], ids) }));
    },
    startIntakeReview: (id) => commitPlanner("심사 시작", (p) => ({ ...p, intakes: iops.startReview(p.intakes ?? [], id, actor()) })),
    closeIntake: (id, status, note) =>
      commitPlanner(status === "rejected" ? "요구사항 반려" : "요구사항 보류", (p) => {
        const intakes = iops.closeIntake(p.intakes ?? [], id, status, note, actor());
        return intakes === p.intakes ? null : { ...p, intakes };
      }),
    reopenIntake: (id) => commitPlanner("요구사항 재심사", (p) => ({ ...p, intakes: iops.reopenIntake(p.intakes ?? [], id) })),
    acceptIntakeAsTask: (id, opts) => {
      let taskId: string | null = null;
      commitPlanner("요구사항 승인(신규 작업)", (p) => {
        const r = iops.acceptIntakeAsNewTask({ intakes: p.intakes ?? [], executionTasks: p.executionTasks }, id, { ...opts, by: actor() });
        if (!r) return null;
        taskId = r.taskId;
        return { ...p, intakes: r.intakes, executionTasks: r.executionTasks };
      });
      return taskId;
    },
    mergeIntakeIntoTask: (id, targetTaskId, opts) =>
      commitPlanner("요구사항 승인(기존 작업 병합)", (p) => {
        const r = iops.mergeIntakeIntoTask({ intakes: p.intakes ?? [], executionTasks: p.executionTasks }, id, targetTaskId, { ...opts, by: actor() });
        return r ? { ...p, intakes: r.intakes, executionTasks: r.executionTasks } : null;
      }),

    /* ---------------------------------------------------------------- Records */

    addRecord: (kind, partial, opts) => {
      const s = get();
      const selfName =
        s.previewPlanRole === "contributor" && s.previewSelfResourceId
          ? useCatalogStore.getState().resources[s.previewSelfResourceId]?.displayName ?? ""
          : "";
      const authored = partial?.author ? partial : { ...partial, author: selfName || actor() };
      let rec = rops.makeRecord({ kind, seq: rops.nextSeq(s.project.records, kind), ...authored });
      rec = rops.logRecordActivity(rec, actor(), "생성", rec.createdAt);
      commitPlanner(`${rops.RECORD_KIND_LABEL[kind]} 추가`, (p) => ({ ...p, records: [...p.records, rec] }));
      if (opts?.open !== false) set({ recordFocusId: rec.id, recordFocusIsNew: true });
      return rec.id;
    },
    updateRecord: (id, patch) => {
      const s = get();
      const cur = s.project.records.find((r) => r.id === id);
      if (!cur) return;
      const message = rops.describeRecordPatch(cur, patch, s.project.records);
      commitPlanner(`${rops.recordCode(cur)} 변경`, (p) => {
        const now = new Date().toISOString();
        let records = p.records.map((r) => {
          if (r.id !== id) return r;
          let next = rops.withClosedAt({ ...r, ...patch, id, kind: r.kind, seq: r.seq, updatedAt: now }, now);
          if (message) next = rops.logRecordActivity(next, actor(), message, now);
          return next;
        });
        records = rops.applySupersede(records, id, actor(), now);
        return { ...p, records };
      });
    },
    deleteRecords: (ids) => {
      if (!ids.length) return;
      const wbs = wbsOf(get().derived.schedule);
      commitPlanner(`기록 ${ids.length}개 삭제`, (p) => {
        const marked = dops.tombstoneTargets(p, "record", ids, dops.catalogOf(p, wbs));
        return { ...marked, records: rops.removeRecords(marked.records, ids) };
      });
      const s = get();
      if (s.recordFocusId && ids.includes(s.recordFocusId)) set({ recordFocusId: null, recordFocusIsNew: false });
    },
    addRecordComment: (id, body, options) => patchRecord(id, options?.parentId ? "대댓글" : "댓글", (r) => xops.addComment(r, actor(), body, options)),
    updateRecordComment: (id, commentId, body, options) => patchRecord(id, "댓글 수정", (r) => xops.updateComment(r, commentId, body, options)),
    deleteRecordComment: (id, commentId) => patchRecord(id, "댓글 삭제", (r) => xops.deleteComment(r, commentId)),
    reviseRecord: (id) => {
      const s = get();
      const rev = rops.makeRevision(s.project.records, id);
      if (!rev) return null;
      const src = s.project.records.find((r) => r.id === id)!;
      const rec = rops.logRecordActivity(rev, actor(), `${rops.recordCode(src)} 개정안 생성`, rev.createdAt);
      commitPlanner(`${rops.recordCode(src)} 개정`, (p) => ({ ...p, records: [...p.records, rec] }));
      set({ recordFocusId: rec.id, recordFocusIsNew: false });
      return rec.id;
    },
    raiseIssueFromRisk: (id) => {
      const s = get();
      const issue = rops.makeIssueFromRisk(s.project.records, id);
      if (!issue) return null;
      const risk = s.project.records.find((r) => r.id === id)!;
      const rec = rops.logRecordActivity(issue, actor(), `${rops.recordCode(risk)} 발생으로 등록`, issue.createdAt);
      commitPlanner(`${rops.recordCode(risk)} 이슈 등록`, (p) => {
        const now = new Date().toISOString();
        const records = p.records.map((r) =>
          r.id === id
            ? rops.logRecordActivity({ ...r, status: "occurred" as const, linkedRecordIds: [...new Set([...r.linkedRecordIds, rec.id])], updatedAt: now }, actor(), `${rops.recordCode(rec)}로 이슈 등록`, now)
            : r,
        );
        return { ...p, records: [...records, rec] };
      });
      set({ recordFocusId: rec.id, recordFocusIsNew: false });
      return rec.id;
    },
    createTaskForRecord: (id, opts) => {
      const s = get();
      const rec = s.project.records.find((r) => r.id === id);
      if (!rec) return null;
      const maxOrder = s.project.executionTasks.reduce((m, t) => Math.max(m, t.order), -1);
      let task = xops.makeExecutionTask({
        order: maxOrder + 1,
        title: opts?.title?.trim() || `[${rops.recordCode(rec)}] ${rec.title || rops.RECORD_KIND_LABEL[rec.kind]}`,
        status: "notStarted",
        assignees: [...rec.assignees],
        ...(rec.due && rops.hasSeverity(rec.kind) ? { due: rec.due } : {}),
        linkedTaskIds: [...rec.linkedTaskIds],
      });
      task = xops.logActivity(task, actor(), `${rops.recordCode(rec)}에서 생성`, task.createdAt);
      commitPlanner(`${rops.recordCode(rec)} 대응 Task 추가`, (p) => {
        const now = new Date().toISOString();
        const records = p.records.map((r) =>
          r.id === id ? rops.logRecordActivity({ ...r, linkedExecutionTaskIds: [...r.linkedExecutionTaskIds, task.id], updatedAt: now }, actor(), `대응 Task 추가: ${task.title}`, now) : r,
        );
        return { ...p, executionTasks: [...p.executionTasks, task], records };
      });
      // open the Task sheet in creation mode over the record (cancel removes the task and its record link,
      // save/cancel return to the record sheet)
      if (opts?.open !== false) {
        set({
          executionFocusId: task.id,
          executionFocusIsNew: true,
          executionReturnRecordId: id,
          executionFocusTab: "details",
          executionFocusCommentId: null,
          recordFocusId: null,
          recordFocusIsNew: false,
        });
      }
      return task.id;
    },
    setRecordFocus: (id, opts) =>
      set({
        recordFocusId: id,
        recordFocusIsNew: id ? !!opts?.isNew : false,
        recordFocusTab: id ? (opts?.commentId ? "comments" : (opts?.tab ?? "details")) : "details",
        recordFocusCommentId: id ? (opts?.commentId ?? null) : null,
        ...(id ? { schedulePeekId: null } : {}),
      }),
    setSchedulePeek: (id) => {
      if (!id) {
        set({ schedulePeekId: null });
        return;
      }
      if (!get().project.tasks.some((task) => task.id === id)) return;
      set({
        schedulePeekId: id,
        executionFocusId: null,
        executionFocusIsNew: false,
        executionFocusCommentId: null,
        recordFocusId: null,
        recordFocusIsNew: false,
      });
    },
    appendSampleRecords: () => {
      let added = 0;
      commitPlanner("샘플 기록 추가", (p) => {
        const merged = rops.appendRecords(p.records, sampleErpRecords());
        const records = rops.pruneRecordLinks(merged, { taskIds: p.tasks.map((t) => t.id), executionTaskIds: p.executionTasks.map((t) => t.id) });
        added = records.length - p.records.length;
        return { ...p, records };
      });
      return added;
    },

    addBucket: (name) => {
      let id = "";
      commitPlanner("버킷 추가", (p) => {
        const r = xops.addBucket(p.planner, name);
        id = r.id;
        return { ...p, planner: r.settings };
      });
      return id;
    },
    renameBucket: (id, name) => commitPlanner("버킷 이름 변경", (p) => ({ ...p, planner: xops.renameBucket(p.planner, id, name) })),
    moveBucket: (id, direction) => commitPlanner("버킷 순서 변경", (p) => ({ ...p, planner: xops.moveBucket(p.planner, id, direction) })),
    reorderBucket: (id, toIndex) => commitPlanner("버킷 순서 변경", (p) => ({ ...p, planner: xops.reorderBucket(p.planner, id, toIndex) })),
    removeBucket: (id) => commitPlanner("버킷 삭제", (p) => ({ ...p, ...xops.removeBucket(p, id) })),
    addLabel: (name, color) => {
      let id = "";
      commitPlanner("라벨 추가", (p) => {
        const r = xops.addLabel(p.planner, name, color);
        id = r.id;
        return { ...p, planner: r.settings };
      });
      return id;
    },
    updateLabel: (id, patch) => commitPlanner("라벨 변경", (p) => ({ ...p, planner: xops.updateLabel(p.planner, id, patch) })),
    reorderLabel: (id, toIndex) => commitPlanner("라벨 순서 변경", (p) => ({ ...p, planner: xops.reorderLabel(p.planner, id, toIndex) })),
    removeLabel: (id) => commitPlanner("라벨 삭제", (p) => ({ ...p, ...xops.removeLabel(p, id) })),
    addCustomField: (def) => {
      let id = "";
      commitPlanner("사용자 정의 필드 추가", (p) => {
        const r = xops.addCustomField(p.planner, def);
        id = r.id;
        return { ...p, planner: r.settings };
      });
      return id;
    },
    updateCustomField: (id, patch) => commitPlanner("사용자 정의 필드 변경", (p) => ({ ...p, planner: xops.updateCustomField(p.planner, id, patch) })),
    removeCustomField: (id) => commitPlanner("사용자 정의 필드 삭제", (p) => ({ ...p, ...xops.removeCustomField(p, id) })),

    /* ---------------------------------------------------------------- Resource assignments & M/M */

    assignResource: (assignment) => {
      let id = "";
      commitPlanner("인력 배정 추가", (p) => {
        id = ops.newId("asg");
        const list = Array.isArray(p.resourceAssignments) ? p.resourceAssignments : [];
        return {
          ...p,
          resourceAssignments: [...list, { ...assignment, id }],
        };
      });
      return id;
    },
    updateAssignment: (id, patch) =>
      commitPlanner("인력 배정 변경", (p) => ({
        ...p,
        resourceAssignments: (p.resourceAssignments || []).map((a) => (a.id === id ? { ...a, ...patch } : a)),
      })),
    removeAssignment: (id) =>
      commitPlanner("인력 배정 삭제", (p) => ({
        ...p,
        resourceAssignments: (p.resourceAssignments || []).filter((a) => a.id !== id),
      })),

    /* ---------------------------------------------------------------- Solutions */

    addSolution: (solution) => {
      let id = "";
      commitPlanner("솔루션 추가", (p) => {
        id = ops.newId("sol");
        const list = Array.isArray(p.solutions) ? p.solutions : [];
        return {
          ...p,
          solutions: [...list, { ...solution, id }],
        };
      });
      return id;
    },
    updateSolution: (id, patch) =>
      commitPlanner("솔루션 변경", (p) => ({
        ...p,
        solutions: (p.solutions || []).map((s) => (s.id === id ? { ...s, ...patch } : s)),
      })),
    removeSolution: (id) =>
      commitPlanner("솔루션 삭제", (p) => ({
        ...p,
        solutions: (p.solutions || []).filter((s) => s.id !== id),
      })),

    /* ---------------------------------------------------------------- Budget & Cost Options */

    updateBudget: (patch) =>
      commitPlanner("예산 정보 변경", (p) => ({
        ...p,
        budget: { ...p.budget, ...patch },
      })),
    updateCostOptions: (patch) =>
      commitPlanner("비용 산정 옵션 변경", (p) => ({
        ...p,
        budget: {
          ...p.budget,
          costOptions: { ...p.budget.costOptions, ...patch },
        },
      })),
    setViewRole: (role) => {
      get().setUI({ viewRole: role });
    },
    setMaskRates: (mask) => {
      get().setUI({ maskRates: mask });
    },
    setPreviewPlanRole: (role) => {
      set({ previewPlanRole: role, previewWorkspaceAdmin: false });
    },
    setPreviewWorkspaceAdmin: (on) => {
      set({ previewWorkspaceAdmin: on, previewPlanRole: on ? null : get().previewPlanRole, previewWorkspaceMember: false });
    },
    createDocument: (kind = "memo") => {
      let id = "";
      commitPlanner("문서 추가", (p) => {
        const made = dops.createDocument(p, { kind, authorId: actor() });
        id = made.document.id;
        return made.project;
      });
      return id;
    },
    saveDocumentBody: (id, blocks) => {
      if (documentWriteBlocked()) return false;
      const s = get();
      const next = dops.saveDocumentBody(s.project, id, blocks, actor(), dops.catalogOf(s.project, wbsOf(s.derived.schedule)));
      if (!next) return false;
      set({ project: next, lastError: null });
      persist();
      return true;
    },
    patchDocument: (id, patch) => {
      commitPlanner("문서 변경", (p) => {
        const next = dops.patchDocument(p, id, patch, actor());
        return next && next !== p ? next : null;
      });
    },
    setDocumentVisibility: (id, visibility) => {
      commitPlanner("문서 구분", (p) => dops.setDocumentVisibility(p, id, visibility, actor(), actor()));
    },
    trashDocument: (id) => {
      commitPlanner("문서 휴지통", (p) => dops.trashDocument(p, id, actor()));
    },
    restoreDocument: (id) => {
      commitPlanner("문서 복원", (p) => dops.restoreDocument(p, id, actor()));
    },
    purgeDocument: (id) => {
      commitPlanner("문서 삭제", (p) => dops.purgeDocument(p, id));
    },
    linkDocument: (id, targetKind, targetId) => {
      const wbs = wbsOf(get().derived.schedule);
      commitPlanner("문서 연결", (p) => {
        const next = dops.linkDocument(p, id, targetKind, targetId, actor(), dops.catalogOf(p, wbs));
        return next && next !== p ? next : null;
      });
    },
    unlinkDocument: (id, linkId) => {
      const wbs = wbsOf(get().derived.schedule);
      commitPlanner("문서 연결 해제", (p) => dops.unlinkDocument(p, id, linkId, actor(), dops.catalogOf(p, wbs)), { keepBody: false });
    },
    setDocumentAttachments: (id, attachments) => {
      const doc = get().project.documents.find((item) => item.id === id);
      const removed = !!doc && doc.attachments.some((item) => !attachments.some((next) => next.id === item.id));
      commitPlanner("문서 첨부", (p) => dops.setDocumentAttachments(p, id, attachments, actor()), { keepBody: !removed });
    },
    addDocumentComment: (id, body, options) => {
      commitPlanner("문서 댓글", (p) => dops.commentOnDocument(p, id, actor(), body, options));
    },
    updateDocumentComment: (id, commentId, body, options) => {
      commitPlanner("문서 댓글 수정", (p) => dops.updateDocumentComment(p, id, commentId, body, actor(), options));
    },
    deleteDocumentComment: (id, commentId) => {
      const role = get().previewPlanRole ?? "owner";
      const status = get().project.lifecycle ?? "active";
      const allowOthers = can(planActor(role), "plan.comment.deleteOthers", { status });
      commitPlanner("문서 댓글 삭제", (p) => dops.deleteDocumentComment(p, id, commentId, actor(), undefined, { allowOthers }));
    },
    createWorkFromDocument: (input) => {
      let made: { taskId?: string; recordId?: string } | null = null;
      const wbs = wbsOf(get().derived.schedule);
      const ok = commitPlanner(
        "문서에서 만들기",
        (p) => {
          const result = dops.createWorkFromDocument(p, { ...input, actor: actor(), catalog: dops.catalogOf(p, wbs) });
          if (!result) return null;
          made = { ...(result.taskId ? { taskId: result.taskId } : {}), ...(result.recordId ? { recordId: result.recordId } : {}) };
          return result.project;
        },
        { keepBody: false },
      );
      return ok ? made : null;
    },
    abandonBlankDocument: (id) => {
      const s = get();
      const result = dops.discardIfBlank(s.project, id);
      if (!result.discarded) return;
      const top = s.past.at(-1);
      const pop = !!top && dops.blankCreateStep(top, s.project, id);
      const pastKeep = s.pastKeepBody.length === s.past.length ? s.pastKeepBody : s.past.map(() => true);
      set({
        project: result.project,
        ...(pop ? { past: s.past.slice(0, -1), pastKeepBody: pastKeep.slice(0, -1) } : {}),
      });
      persist();
    },
    setPreviewSelfResourceId: (id) => {
      set({ previewSelfResourceId: id });
    },
    setPreviewGuest: (on) => {
      set({ previewGuest: on, previewGuestPlanId: on ? get().activeId : null, previewWorkspaceMember: false });
    },
    setPreviewWorkspaceMember: (on) => {
      set({ previewWorkspaceMember: on, previewWorkspaceAdmin: false, previewGuest: false, previewGuestPlanId: null });
    },
  };
});

/** Human-readable activity line for a field patch (null → nothing worth logging). */
function describePatch(cur: ExecutionTask, patch: Partial<ExecutionTask>, project: Project): string | null {
  const parts: string[] = [];
  if ("title" in patch && patch.title !== cur.title) parts.push(`제목 → ${patch.title || "(없음)"}`);
  if ("status" in patch && patch.status !== cur.status) parts.push(`상태 → ${patch.status ? xops.STATUS_LABEL[patch.status] : "미정"}`);
  if ("priority" in patch && patch.priority !== cur.priority) parts.push(`우선순위 → ${patch.priority ? xops.PRIORITY_LABEL[patch.priority] : "미정"}`);
  if ("bucketId" in patch && patch.bucketId !== cur.bucketId) parts.push(`버킷 → ${project.planner.buckets.find((b) => b.id === patch.bucketId)?.name ?? "없음"}`);
  if ("assignees" in patch && patch.assignees && patch.assignees.join("|") !== cur.assignees.join("|")) parts.push(`담당 → ${patch.assignees.length ? patch.assignees.join(", ") : "미정"}`);
  if ("start" in patch && patch.start !== cur.start) parts.push(`시작 → ${formatDate(patch.start, "numeric", { empty: "미정" })}`);
  if ("due" in patch && patch.due !== cur.due) parts.push(`마감 → ${formatDate(patch.due, "numeric", { empty: "미정" })}`);
  if ("linkedTaskIds" in patch && patch.linkedTaskIds && patch.linkedTaskIds.join("|") !== cur.linkedTaskIds.join("|")) parts.push(`연결 범위 ${patch.linkedTaskIds.length}개`);
  if ("labelIds" in patch && patch.labelIds && patch.labelIds.join("|") !== cur.labelIds.join("|")) parts.push("라벨 변경");
  if ("recurrence" in patch) parts.push(`반복 → ${xops.describeRecurrence(patch.recurrence)}`);
  if ("notes" in patch && patch.notes !== cur.notes) parts.push("메모 수정");
  if ("showChecklistOnCard" in patch && patch.showChecklistOnCard !== cur.showChecklistOnCard) parts.push(patch.showChecklistOnCard ? "카드에 체크리스트 표시" : "카드에 체크리스트 숨김");
  if ("coverAttachmentId" in patch && patch.coverAttachmentId !== cur.coverAttachmentId) parts.push(patch.coverAttachmentId ? "커버 이미지 설정" : "커버 이미지 제거");
  if ("customFields" in patch) parts.push("필드 값 변경");
  return parts.length ? parts.join(" · ") : null;
}

/** Visible rows honoring collapsed summaries, in document order. */
export function selectVisibleRows(state: Pick<State, "derived" | "collapsed">): string[] {
  const { schedule } = state.derived;
  const out: string[] = [];
  const hidden = new Set<string>();
  for (const id of schedule.order) {
    const parent = findParent(state, id);
    if (parent && (hidden.has(parent) || state.collapsed.has(parent))) {
      hidden.add(id);
      continue;
    }
    out.push(id);
  }
  return out;
}

export function parentOf(state: Pick<State, "derived">, id: string): string | null {
  return findParent(state, id);
}

const parentCache = new WeakMap<Schedule, Map<string, string | null>>();
function findParent(state: Pick<State, "derived">, id: string): string | null {
  const sched = state.derived.schedule;
  let m = parentCache.get(sched);
  if (!m) {
    m = new Map();
    // rebuild from depth information in document order
    const stack: string[] = [];
    for (const tid of sched.order) {
      const d = sched.tasks.get(tid)!.depth;
      stack.length = d;
      m.set(tid, stack[d - 1] ?? null);
      stack[d] = tid;
    }
    parentCache.set(sched, m);
  }
  return m.get(id) ?? null;
}
